import { useState, useEffect, type FormEvent } from 'react';
import { Card, CardContent, Button, Input, Select } from "@repo/o37-ui";
import { Globe, Trash2 } from "lucide-react";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

type DomainGroup = {
  id: string;
  name: string;
  domains: string[];
  created_at: string;
  domain_group_permissions?: { access_level: string }[];
  currentUserRole?: string;
};

export default function DomainGroups() {
  const [groups, setGroups] = useState<DomainGroup[]>([]);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<DomainGroup | null>(null);
  const [sharingGroup, setSharingGroup] = useState<DomainGroup | null>(null);

  const userRole = localStorage.getItem('app_role') || 'user';
  const userId = localStorage.getItem('app_user_id') || '';

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      setError('');
      const response = await fetch(`${API_URL}/domain-groups`, {
        headers: {
            'x-user-role': userRole,
            'x-user-id': userId
        }
      });
      if (response.status === 403) {
          setError('Access Denied: You do not have permission to view domain groups.');
          setGroups([]);
          return;
      }
      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      } else {
        const errText = await response.text();
        console.error("Domain Group Fetch Error:", errText);
        try {
            const errJson = JSON.parse(errText);
            setError(errJson.error || `Failed to load domain groups (${response.status})`);
        } catch {
            setError(`Failed to load domain groups: ${response.status} ${response.statusText}`);
        }
      }
    } catch (e) {
      console.error(e);
      setError('Error connecting to server');
    }
  };

  const handleCreate = async (data: any) => {
      try {
        const response = await fetch(`${API_URL}/domain-groups`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'x-user-role': userRole,
              'x-user-id': userId
            },
            body: JSON.stringify(data)
        });
        if (response.ok) {
            setShowCreateModal(false);
            loadGroups();
        } else {
            const err = await response.json();
            alert(err.error);
        }
      } catch (e) {
          alert('Failed to create group');
      }
  };

  const handleUpdate = async (id: string, data: any) => {
    try {
      const response = await fetch(`${API_URL}/domain-groups/${id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'x-user-role': userRole,
          'x-user-id': userId
        },
        body: JSON.stringify(data)
      });
      if (response.ok) {
        setEditingGroup(null);
        loadGroups();
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to update group');
      }
    } catch (e) {
      alert('Failed to update group');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this group?')) return;
    try {
      const response = await fetch(`${API_URL}/domain-groups/${id}`, {
        method: 'DELETE',
        headers: { 
          'x-user-role': userRole,
          'x-user-id': userId
        }
      });
      if (response.ok) {
        loadGroups();
      } else {
        alert('Failed to delete group');
      }
    } catch (e) {
      alert('Failed to delete group');
    }
  };

  const isSuperAdmin = userRole === 'super_admin';

  return (
    <div className="min-h-full">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="text-2xl font-semibold mb-2">Domain Groups</h1>
          <p className="text-neutral-500">Manage allowable email domains for access control.</p>
        </div>
        {isSuperAdmin && (
          <Button onClick={() => setShowCreateModal(true)} text="Create Group" />
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      {groups.length === 0 && !error ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-12 h-12 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe className="w-6 h-6 text-neutral-400" />
            </div>
            <h3 className="text-lg font-medium text-neutral-900 mb-1">No Domain Groups</h3>
            <p className="text-neutral-500 mb-4">
              {isSuperAdmin 
                ? "Create your first domain group to control email-based access."
                : "You don't have access to any domain groups yet."}
            </p>
            {isSuperAdmin && (
              <Button onClick={() => setShowCreateModal(true)} text="Create Group" />
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {groups.map((group) => {
            const accessLevel = group.domain_group_permissions?.[0]?.access_level || (isSuperAdmin ? 'admin' : 'viewer');
            const canEdit = isSuperAdmin || accessLevel === 'editor' || accessLevel === 'admin';
            const canAdmin = isSuperAdmin || accessLevel === 'admin';
            
            return (
              <Card key={group.id} className="hover:border-neutral-300 transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Globe className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-neutral-900">{group.name}</h3>
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${
                            accessLevel === 'admin' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                            accessLevel === 'editor' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                            'bg-blue-50 text-blue-700 border-blue-200'
                          }`}>
                            {accessLevel.charAt(0).toUpperCase() + accessLevel.slice(1)}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {group.domains.slice(0, 5).map(d => (
                            <span key={d} className="inline-block bg-neutral-100 rounded-md px-2 py-1 text-xs text-neutral-600 border border-neutral-200">
                              {d}
                            </span>
                          ))}
                          {group.domains.length > 5 && (
                            <span className="inline-block bg-neutral-50 rounded-md px-2 py-1 text-xs text-neutral-400">
                              +{group.domains.length - 5} more
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-neutral-400 mt-2">
                          Created {new Date(group.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {canAdmin && (
                        <Button 
                          variant="outline"
                          className="h-8 text-xs px-3"
                          onClick={() => setSharingGroup(group)}
                          text="Share"
                        />
                      )}
                      {canEdit && (
                        <Button 
                          variant="secondary"
                          className="h-8 text-xs px-3"
                          onClick={() => setEditingGroup(group)}
                          text="Edit"
                        />
                      )}
                      {canAdmin && (
                        <Button 
                          variant="danger-outline"
                          className="h-8 w-8 p-0 flex items-center justify-center"
                          onClick={() => handleDelete(group.id)}
                          text=""
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {showCreateModal && (
        <GroupModal 
            title="Create Domain Group"
            onClose={() => setShowCreateModal(false)}
            onSubmit={handleCreate}
        />
      )}

      {editingGroup && (
        <GroupModal 
            title="Edit Domain Group"
            initialData={editingGroup}
            onClose={() => setEditingGroup(null)}
            onSubmit={(data) => handleUpdate(editingGroup.id, data)}
        />
      )}

      {sharingGroup && (
        <ShareGroupModal 
            group={sharingGroup}
            onClose={() => { setSharingGroup(null); loadGroups(); }}
        />
      )}
    </div>
  );
}

function GroupModal({ title, initialData, onClose, onSubmit }: { title: string, initialData?: DomainGroup, onClose: () => void, onSubmit: (data: any) => void }) {
    const [name, setName] = useState(initialData?.name || '');
    const [domainsInput, setDomainsInput] = useState(initialData?.domains.join(', ') || '');

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        const domains = domainsInput.split(',').map(d => d.trim()).filter(d => d.length > 0);
        onSubmit({ name, domains });
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
                <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-100">
                    <h2 className="text-lg font-semibold text-neutral-900">{title}</h2>
                    <button onClick={onClose} className="text-neutral-400 hover:text-neutral-900 p-2 rounded-lg hover:bg-neutral-100">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="p-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Group Name</label>
                            <Input 
                                value={name} 
                                onChange={e => setName(e.target.value)} 
                                required 
                                placeholder="e.g. Strategic Partners" 
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1.5">Allowed Domains</label>
                            <textarea 
                              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm min-h-[120px] focus:ring-2 focus:ring-neutral-200 focus:border-neutral-400 outline-none resize-none" 
                              value={domainsInput} 
                              onChange={e => setDomainsInput(e.target.value)} 
                              required 
                              placeholder="@google.com, @microsoft.com, @example.com"
                            />
                            <p className="text-xs text-neutral-400 mt-1.5">Enter email domains separated by commas. Include the @ symbol.</p>
                        </div>
                    </div>
                    <div className="mt-6 flex justify-end gap-3">
                        <Button variant="outline" type="button" onClick={onClose} text="Cancel" />
                        <Button type="submit" text="Save" />
                    </div>
                </form>
            </div>
        </div>
    );
}

function ShareGroupModal({ group, onClose }: { group: DomainGroup, onClose: () => void }) {
    const [permissions, setPermissions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('viewer');
    const [error, setError] = useState('');

    const userRole = localStorage.getItem('app_role') || 'user';
    const userId = localStorage.getItem('app_user_id') || '';

    useEffect(() => {
        loadPermissions();
    }, [group.id]);

    const loadPermissions = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/domain-groups/${group.id}/permissions`, {
                headers: { 'x-user-role': userRole, 'x-user-id': userId }
            });
            if (response.ok) {
                const data = await response.json();
                setPermissions(data);
            } else {
                setError('Failed to load permissions');
            }
        } catch (e) {
            setError('Error loading permissions');
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async (e: FormEvent) => {
        e.preventDefault();
        try {
            const response = await fetch(`${API_URL}/domain-groups/${group.id}/permissions`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'x-user-role': userRole,
                    'x-user-id': userId
                },
                body: JSON.stringify({ email, accessLevel: role })
            });
            
            if (response.ok) {
                setEmail('');
                loadPermissions();
            } else {
                const err = await response.json();
                alert(err.error || 'Failed to add user');
            }
        } catch (e) {
            alert('Failed to add user');
        }
    };

    const handleRemoveUser = async (targetUserId: string) => {
        if (!confirm('Remove access for this user?')) return;
        try {
            const response = await fetch(`${API_URL}/domain-groups/${group.id}/permissions/${targetUserId}`, {
                method: 'DELETE',
                headers: { 'x-user-role': userRole, 'x-user-id': userId }
            });
            
            if (response.ok) {
                loadPermissions();
            } else {
                alert('Failed to remove user');
            }
        } catch (e) {
            alert('Failed to remove user');
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
                <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-100">
                    <div>
                        <h2 className="text-lg font-semibold text-neutral-900">Share Domain Group</h2>
                        <p className="text-sm text-neutral-500">{group.name}</p>
                    </div>
                    <button onClick={onClose} className="text-neutral-400 hover:text-neutral-900 p-2 rounded-lg hover:bg-neutral-100">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
                            {error}
                        </div>
                    )}
                    
                    <form onSubmit={handleAddUser} className="mb-6 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                        <h3 className="text-sm font-semibold text-neutral-700 mb-3">Add Team Member</h3>
                        <div className="flex gap-2">
                            <div className="flex-1">
                                <Input 
                                    type="email" 
                                    placeholder="email@example.com" 
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="w-28">
                                <Select 
                                    value={role}
                                    onChange={e => setRole(e.target.value)}
                                >
                                    <option value="viewer">Viewer</option>
                                    <option value="editor">Editor</option>
                                    <option value="admin">Admin</option>
                                </Select>
                            </div>
                            <Button type="submit" text="Add" />
                        </div>
                    </form>

                    <div>
                        <h3 className="text-sm font-semibold text-neutral-700 mb-3">Team Access</h3>
                        {loading ? (
                            <div className="text-center py-4 text-neutral-400 text-sm">Loading...</div>
                        ) : (
                            <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100">
                                {permissions.length === 0 ? (
                                    <div className="p-4 text-center text-neutral-400 text-sm">No specific permissions set.</div>
                                ) : (
                                    permissions.map((perm) => (
                                        <div key={perm.id} className="p-3 flex justify-between items-center">
                                            <div>
                                                <div className="font-medium text-sm text-neutral-900">{perm.profiles?.name || 'Unknown User'}</div>
                                                <div className="text-xs text-neutral-500">{perm.profiles?.email}</div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className={`text-xs px-2 py-1 rounded-md border font-medium ${
                                                    perm.access_level === 'admin' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                                    perm.access_level === 'editor' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                                    'bg-blue-50 text-blue-700 border-blue-200'
                                                }`}>
                                                    {perm.access_level.charAt(0).toUpperCase() + perm.access_level.slice(1)}
                                                </span>
                                                <Button 
                                                    variant="danger-outline"
                                                    className="h-7 text-xs px-2"
                                                    onClick={() => handleRemoveUser(perm.user_id)}
                                                    text="Remove"
                                                />
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-neutral-100 bg-neutral-50 flex justify-end">
                    <Button variant="outline" onClick={onClose} text="Done" />
                </div>
            </div>
        </div>
    );
}
